import React, { useState } from "react";
import { BarChart2, RefreshCw } from "lucide-react";
import type { Site } from "../../../groups/types/Emailstypes";
import { useSitePerformance } from "../../hooks/useSitePerformance";
import KpiGrid              from "./KpiGrid";
import TrendChart           from "./TrendChart";
import TopFichesCard        from "./TopFichesCard";
import ImpressionsBarChart  from "./ImpressionsBarChart";
import PeriodSelector       from "./PeriodSelector";

 import type { DateRange } from "../../types/dashboard.types";
 
import { useDashboardExport }  from "../../hooks/useDashboardExport";

import CompareToggle       from "./CompareToggle";
import ExportMenu          from "./ExportMenu";

const todayStr = () => new Date().toISOString().slice(0, 10);
const daysAgo  = (n: number) => {
  const d = new Date(); d.setDate(d.getDate() - n); return d.toISOString().slice(0, 10);
};
const shiftBack = (r: DateRange): DateRange => {
  const ms   = new Date(r.endDate).getTime() - new Date(r.startDate).getTime();
  const days = Math.round(ms / 86_400_000) + 1;
  return { startDate: daysAgo(days * 2 - 1 + days - 1), endDate: daysAgo(days) };
};

interface Props { site: Site }

const SitePerformancePanel: React.FC<Props> = ({ site }) => {
  const [activePeriod,    setActivePeriod]    = useState(30);
  const [dateRange,       setDateRange]       = useState<DateRange>({ startDate: daysAgo(29), endDate: todayStr() });
  const [compareEnabled,  setCompareEnabled]  = useState(false);
  const [compareRange,    setCompareRange]    = useState<DateRange>(() => shiftBack({ startDate: daysAgo(29), endDate: todayStr() }));
  const [userEditedCmp,   setUserEditedCmp]   = useState(false);

  const handleToggleCompare = () => {
    setCompareEnabled(e => {
      if (!e) { setUserEditedCmp(false); setCompareRange(shiftBack(dateRange)); }
      return !e;
    });
  };

  const primary = useSitePerformance({
    accountId: site.globalAccountId, subjectEmail: site.subjectEmail,
    startDate: dateRange.startDate,  endDate: dateRange.endDate, enabled: true,
  });

  const compare = useSitePerformance({
    accountId: site.globalAccountId, subjectEmail: site.subjectEmail,
    startDate: compareRange.startDate, endDate: compareRange.endDate,
    enabled: compareEnabled,
  });

  const primaryLabel = `${dateRange.startDate} → ${dateRange.endDate}`;
  const compareLabel = `${compareRange.startDate} → ${compareRange.endDate}`;

  const { exporting, exportCSV, exportPDF } = useDashboardExport({
    site,
    data:           primary.data,
    compareData:    compare.data,
    dateRange,
    compareRange,
    compareEnabled,
  });
 
   if (primary.isLoading) return (
    <div className="space-y-4 animate-pulse">
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        {[...Array(4)].map((_, i) => <div key={i} className="h-20 rounded-2xl bg-slate-100" />)}
      </div>
      <div className="h-64 rounded-2xl bg-slate-100" />
    </div>
  );

   if (primary.isError) return (
    <div className="flex flex-col items-center justify-center py-16 text-center">
      <div className="w-12 h-12 rounded-2xl bg-rose-50 flex items-center justify-center mx-auto mb-3">
        <BarChart2 size={22} className="text-rose-400" />
      </div>
      <p className="text-sm font-semibold text-slate-600">Données non disponibles</p>
      <button onClick={() => primary.refetch()}
        className="mt-4 px-4 py-2 rounded-xl bg-[#0F52BA] text-white text-xs font-semibold hover:bg-blue-700 transition-colors">
        Réessayer
      </button>
    </div>
  );

  const k  = primary.data?.kpis;
  const ck = compare.data?.kpis;
  const perLocation = primary.data?.perLocation ?? [];

  return (
    <div className="space-y-5">

       <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center gap-2">
          <div className="w-7 h-7 rounded-lg flex items-center justify-center text-white text-xs font-bold"
            style={{ backgroundColor: site.color }}>
            {site.icon}
          </div>
          <div>
            <h2 className="text-[15px] font-bold text-slate-800">{site.label}</h2>
            <p className="text-[11px] text-slate-400">
              {primary.data?.locationCount ?? "—"} fiches · {primaryLabel}
            </p>
          </div>
          {primary.isFetching && !primary.isLoading && (
            <RefreshCw size={13} className="text-blue-400 animate-spin ml-1" />
          )}
        </div>

        <div className="flex flex-wrap items-center gap-2">
<PeriodSelector
  activePeriod={activePeriod}
  dateRange={dateRange}
  onChange={(range, period) => {
    setDateRange(range);
    setActivePeriod(period ?? activePeriod);
    if (!userEditedCmp) setCompareRange(shiftBack(range));
  }}
/>          <div className="w-px h-4 bg-slate-200 hidden sm:block" />
          <CompareToggle
            enabled={compareEnabled}
            onToggle={handleToggleCompare}
            compareRange={compareRange}
            onChange={r => { setCompareRange(r); setUserEditedCmp(true); }}
          />
          <div className="w-px h-4 bg-slate-200 hidden sm:block" />
          <ExportMenu onExportCSV={exportCSV} onExportPDF={exportPDF} exporting={exporting} />
        </div>
      </div>

       {compareEnabled && (
        <div className="flex items-center gap-2 px-4 py-2.5 rounded-xl bg-indigo-50 border border-indigo-100 text-[11px]">
          <div className="w-2 h-2 rounded-full bg-[#0F52BA]" />
          <span className="font-semibold text-indigo-700">{primaryLabel}</span>
          <span className="text-indigo-400">vs</span>
          <div className="w-2 h-2 rounded-full bg-indigo-400 border-2 border-dashed border-indigo-400" />
          <span className="font-semibold text-indigo-500">{compareLabel}</span>
          {compare.isFetching && <RefreshCw size={10} className="text-indigo-400 animate-spin ml-1" />}
        </div>
      )}

       <KpiGrid
        totalImpressions={k?.totalImpressions   ?? 0}
        callClicks={k?.callClicks               ?? 0}
        websiteClicks={k?.websiteClicks         ?? 0}
        directionRequests={k?.directionRequests ?? 0}
        compareEnabled={compareEnabled}
        compareTotalImpressions={ck?.totalImpressions}
        compareCallClicks={ck?.callClicks}
        compareWebsiteClicks={ck?.websiteClicks}
        compareDirectionRequests={ck?.directionRequests}
      />

       {primary.data?.series && (
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
          <TrendChart
            series={primary.data.series}
            compareSeries={compareEnabled ? compare.data?.series : undefined}
            compareEnabled={compareEnabled}
            primaryLabel={primaryLabel}
            compareLabel={compareLabel}
          />
          <TopFichesCard locations={perLocation} />
        </div>
      )}

      <ImpressionsBarChart locations={perLocation} />
    </div>
  );
};

export default SitePerformancePanel;